/**
 * Role 10 — Statement of Financial Position (Liquidity) — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role10 (220000 - Statement of Financial Position, Liquidity ordering) totals.
 *
 * All qnames are `ssmt:` prefixed with the `Liquidity` suffix on the field name.
 * The component owns column-visibility resolution via `resolveRole10VisibleSuffixes`
 * and passes the resolved suffix list in.
 *
 * @param formData - The form data object (control name → value)
 * @param suffixes - Resolved column suffixes
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_220000(
  formData: Record<string, any>,
  suffixes: ReadonlyArray<string>,
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  const num = (key: string): number => {
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  for (const suffix of suffixes) {
    // Total Assets (liquidity order)
    const totalAssets =
      num(`ssmt:CashAndCashEquivalentsLiquidity${suffix}`) +
      num(`ssmt:ShortTermInvestmentsLiquidity${suffix}`) +
      num(`ssmt:TradeReceivablesLiquidity${suffix}`) +
      num(`ssmt:OtherReceivablesLiquidity${suffix}`) +
      num(`ssmt:InventoriesLiquidity${suffix}`) +
      num(`ssmt:PrepaymentsLiquidity${suffix}`) +
      num(`ssmt:PropertyPlantAndEquipmentLiquidity${suffix}`) +
      num(`ssmt:InvestmentPropertiesLiquidity${suffix}`) +
      num(`ssmt:IntangibleAssetsLiquidity${suffix}`) +
      num(`ssmt:InvestmentsInSubsidiariesLiquidity${suffix}`) +
      num(`ssmt:InvestmentsInAssociatesLiquidity${suffix}`) +
      num(`ssmt:InvestmentsInJointVenturesLiquidity${suffix}`) +
      num(`ssmt:LongTermInvestmentsLiquidity${suffix}`) +
      num(`ssmt:DeferredTaxAssetsLiquidity${suffix}`) +
      num(`ssmt:OtherAssetsLiquidity${suffix}`);
    calculatedTotals[`ssmt:TotalAssetsLiquidity${suffix}`] = totalAssets;

    // Total Liabilities (maturity order)
    const totalLiabilities =
      num(`ssmt:BankOverdraftsLiquidity${suffix}`) +
      num(`ssmt:TradePayablesLiquidity${suffix}`) +
      num(`ssmt:OtherPayablesLiquidity${suffix}`) +
      num(`ssmt:ShortTermBorrowingsLiquidity${suffix}`) +
      num(`ssmt:CurrentTaxLiabilitiesLiquidity${suffix}`) +
      num(`ssmt:ShortTermProvisionsLiquidity${suffix}`) +
      num(`ssmt:LongTermBorrowingsLiquidity${suffix}`) +
      num(`ssmt:DeferredTaxLiabilitiesLiquidity${suffix}`) +
      num(`ssmt:LongTermProvisionsLiquidity${suffix}`) +
      num(`ssmt:OtherLiabilitiesLiquidity${suffix}`);
    calculatedTotals[`ssmt:TotalLiabilitiesLiquidity${suffix}`] =
      totalLiabilities;

    // Total Equity
    const totalEquity =
      num(`ssmt:ShareCapitalLiquidity${suffix}`) +
      num(`ssmt:SharePremiumLiquidity${suffix}`) +
      num(`ssmt:RetainedEarningsLiquidity${suffix}`) +
      num(`ssmt:OtherReservesLiquidity${suffix}`);
    calculatedTotals[`ssmt:TotalEquityLiquidity${suffix}`] = totalEquity;

    // Total Equity and Liabilities
    calculatedTotals[`ssmt:TotalEquityAndLiabilitiesLiquidity${suffix}`] =
      totalEquity + totalLiabilities;
  }

  return calculatedTotals;
}
